import { CubePresenter, Logger, NOOP_VOID, Scope } from 'wdc-cube';
import { reuseRows } from '../../../../components/rows';
// Os candidatos vem de DOIS conceitos, conforme o papel de quem monta a selecao:
// o admin escolhe entre os colaboradores da empresa, o profissional entre quem
// compartilhou monitoramento com ele.
import { CollaboratorsApi } from '../collaborators/collaborators.api';
import { MonitoringApi } from '../monitoring/monitoring.api';
import { SleepReportApi } from './sleep-report.api';
import { CollaboratorDetailKeys } from '../collaborators/detail/collaborator-detail.key';
import { CompanySelectorPresenter, MISSING_COMPANY } from '../../../../components/company-selector/company-selector.presenter';
import { PERIOD_LABELS, PeriodSelectorPresenter, recallPeriod } from '../../../../components/period';
import { SleepReportKeys } from './sleep-report.key';
import { SLEEP_MAX_DAYS, SLEEP_SHORTCUTS } from './sleep-report.periods';
import { SleepCandidateScope, SleepOptionScope, SleepReportScope, SleepColumnScope, SleepRowScope, SleepTableScope, SleepTabScope } from './sleep-report.scope';
import { buildSleepReport, daysBetween, gridOf, phaseOfTable, SLEEP_REPORT_TABLES } from './sleep-report.tables';
const LOG = Logger.get('Mormaii.SleepReportPresenter');
/** O nome do relatorio sem grupo - todos os atletas da empresa, ou os marcados deles. */
const ALL_ATHLETES = 'Todos os colaboradores';
/**
 * O teto do PDF (B-44, aprovado em 16/10/2026). Com 2.000 pessoas ele saiu com 324 MB e
 * cortou 1.900 delas em silencio; com uma pessoa por linha, 100 cabem em poucas paginas
 * por tabela. Acima disso, a tela pede para filtrar.
 */
export const SLEEP_PDF_MAX_PEOPLE = 100;
// @Inject
const collaboratorsApi = CollaboratorsApi.INSTANCE;
// @Inject
const monitoringApi = MonitoringApi.INSTANCE;
// @Inject
const api = SleepReportApi.INSTANCE;
/**
 * O relatorio de sono: seis tabelas do mesmo grupo de atletas, num periodo.
 *
 * Desenho que vem do PDF que o cliente usa (`demandas/SONO_03_09_2026.pdf`): linha e
 * dia, coluna e atleta, e as seis tabelas dizem respeito a mesma noite.
 *
 * ## O que viaja no endereco
 *
 * A selecao de atletas NAO. Ela e salva no servidor com um nome, e aqui viaja o id -
 * vinte e um numeros numa URL seriam ilegiveis, e a selecao se repete semana apos
 * semana. Periodo e aba viajam, porque sao escolhas de leitura e um link tem de
 * abrir na mesma tela.
 *
 * ## As seis tabelas ficam TODAS montadas
 *
 * A tela mostra uma por vez, mas o PDF leva as seis, e trocar de aba nao pode custar
 * rede. Uma consulta traz as noites; as seis tabelas saem delas em memoria.
 */
export class SleepReportPresenter extends CubePresenter {
    constructor(app) {
        super(app, new SleepReportScope());
        this.parentSlot = NOOP_VOID;
        this.at = { period: '7d', table: 'deitou' };
        /** As seis, sempre. A aba escolhe qual vai para o scope. */
        this.tables = [];
        this.selections = [];
        /** Os atletas que quem esta logado pode escolher. */
        this.candidates = [];
        /** O que esta marcado no painel de escolha, que so vira selecao ao salvar. */
        this.checked = new Set();
        /**
         * Quem entra no relatorio SEM grupo salvo (B-44, relato 11).
         *
         * Abre com todos os atletas da empresa, e o painel de escolha tira e poe gente ao
         * vivo. Nao vai ao endereco: centenas de ids nao cabem num link, e um F5 volta a
         * "todos". Salvar como grupo continua sendo o jeito de guardar uma escolha.
         */
        this.adhoc = new Set();
        /**
         * As noites que ja vieram, para a consulta em vigor.
         *
         * Sem grupo, a consulta e de TODOS os atletas da empresa, uma vez so; marcar e
         * desmarcar refaz a tabela com o que esta em maos. Refazer a consulta a cada
         * clique custaria 2,4 s com 2.000 pessoas (medido em 16/10/2026).
         */
        this.nights = [];
        /**
         * O seletor de empresa - so o administrador o ve.
         *
         * Trocar de empresa troca os ATLETAS oferecidos, e por isso limpa a selecao
         * aberta: uma selecao pertence a uma empresa, e carrega-la sob outra mostraria
         * nomes que nao estao mais na lista.
         */
        this.selector = new CompanySelectorPresenter(this.update, async (companyId) => {
            this.owner?.rememberFilterCompany(companyId);
            await this.go({ companyId, selectionId: undefined, patientId: undefined });
        }, () => this.app.alert('error', MISSING_COMPANY.title, MISSING_COMPANY.text));
        /**
         * O seletor de periodo.
         *
         * Ele decide o intervalo; o PARAMETRO continua sendo daqui - e por isso o
         * retorno de chamada escreve no endereco, como o seletor de empresa faz.
         */
        this.period = new PeriodSelectorPresenter(this.update, SLEEP_SHORTCUTS, async ({ from, to, shortcut }) => {
            const custom = shortcut === 'custom';
            await this.go({
                period: shortcut,
                from: custom ? from : undefined,
                to: custom ? to : undefined
            });
        }, SLEEP_MAX_DAYS, true);
        this.pending = 0;
    }
    async applyParameters(intent, initialization, last) {
        const keys = new SleepReportKeys(this.app, intent);
        if (!(await keys.owner?.allowEntry('sleep-report', intent))) {
            return false;
        }
        // Atributos da intencao: lidos a cada chamada, como os slots. Vem de quem esta
        // acima na composicao, e reler custa nada.
        this.owner = keys.owner;
        if (initialization) {
            this.scope.onSearchChanged = this.handleSearchChanged.bind(this);
            this.scope.onTogglePicking = this.action(this.onTogglePicking);
            this.scope.onSelectAll = this.action(this.onSelectAll);
            this.scope.onClearAll = this.action(this.onClearAll);
            this.scope.onSaveSelection = this.action(this.onSaveSelection);
            this.scope.onDeleteSelection = this.action(this.onDeleteSelection);
            this.scope.onDownload = this.action(this.onDownload);
            this.scope.onToggleLayout = this.action(this.onToggleLayout);
            this.scope.period = this.period.scope;
            this.scope.tabs = SLEEP_REPORT_TABLES.map((key) => this.tabOf(key));
            // O seletor proprio so existe quando a escolha NAO esta no cabecalho.
            this.scope.companyChooser =
                (keys.owner?.canChooseCompany ?? false) && !(keys.owner?.hasHeaderChooser ?? false);
            if (this.scope.companyChooser) {
                // Sem "Todas as empresas": um relatorio de sono e de um time. O
                // seletor dos outros modulos oferece o vazio porque la a pergunta e
                // "quantos", e somar empresas responde alguma coisa; aqui as colunas
                // sao pessoas, e misturar empresas nao descreve time nenhum.
                this.selector.describe('Empresa', 'Escolha uma empresa');
                /*
                 * Quem administra empresas escolhe entre as DELE; o administrador
                 * geral escolhe entre todas, e ai o seletor busca no servidor.
                 */
                const minhas = keys.owner?.myCompanies ?? [];
                if (minhas.length > 0) {
                    this.selector.restrictTo(minhas);
                }
                await this.selector.showSelected(keys.owner?.companyOnArrival(keys.companyId));
                this.scope.companySelector = this.selector.scope;
            }
            LOG.info('Initialized');
        }
        // O slot e decidido acima na composicao e pode mudar entre viagens: le-se sempre.
        this.parentSlot = keys.parentSlot;
        if (last) {
            this.owner?.showSection('sleep-report', 'Relatório de Sono', 'Seis leituras da mesma noite, por atleta e por dia, prontas para baixar em PDF');
        }
        this.parentSlot(this.scope);
        // Sem empresa no endereco, a PADRAO de quem esta logado: quem administra mais
        // de uma precisa dizer qual, e o servidor recusa agir sem saber.
        const empresa = keys.owner?.companyOnArrival(keys.companyId);
        // Sem periodo no endereco, o ultimo escolhido no painel (B-35). O teto de cinco
        // semanas vale para ele como para qualquer outro.
        const lembrado = keys.periodInAddress ? undefined : recallPeriod(SLEEP_SHORTCUTS);
        const custom = lembrado?.shortcut === 'custom';
        // Os elencos da empresa sao pedidos dentro do `settle` - ver la.
        await this.moveTo({
            companyId: empresa,
            selectionId: keys.selectionId,
            patientId: keys.patientId,
            period: (lembrado?.shortcut ?? keys.period),
            from: lembrado ? (custom ? lembrado.from : undefined) : keys.from,
            to: lembrado ? (custom ? lembrado.to : undefined) : keys.to,
            table: keys.table,
            layout: keys.layout
        });
        return true;
    }
    publishParameters(intent) {
        const keys = new SleepReportKeys(this.app, intent);
        keys.companyId = this.owner?.companyIdInAddress(this.at.companyId);
        keys.selectionId = this.at.selectionId;
        keys.patientId = this.at.patientId;
        keys.period = this.at.period;
        keys.from = this.at.period === 'custom' ? this.at.from : undefined;
        keys.to = this.at.period === 'custom' ? this.at.to : undefined;
        keys.table = this.at.table;
        keys.layout = this.at.layout;
    }
    // ========== ESTADO ==========
    async moveTo(target) {
        this.at = target;
        await this.settle();
    }
    /** Uma viagem para o proprio place: mexe no estado e republica o endereco. */
    async go(changes) {
        this.at = { ...this.at, ...changes };
        await this.settle();
        this.updateHistory();
    }
    /**
     * Poe a tela de acordo com `this.at`.
     *
     * UM caminho, usado pela chegada e por toda troca. Ter dois - um que recarregava
     * os atletas e outro que nao - fazia trocar de empresa manter a lista da
     * anterior, e o painel de escolha abria vazio sem dizer por que.
     */
    async settle() {
        /*
         * Os ELENCOS sao da EMPRESA, e vem antes de tudo: com as selecoes da empresa
         * anterior em maos, aplicar uma delas pediria ao servidor os atletas daquela
         * outra empresa - que e o que fechamos em 17/09/2026.
         *
         * AQUI, e nao so na chegada (B-34, 08/10/2026): escolher a empresa no seletor
         * da propria tela e um `go`, que nao passa pelo `applyParameters`. O
         * administrador geral chegava sem empresa, escolhia uma, e o seletor de grupo
         * ficava "Nenhum grupo salvo" - criar o grupo de novo dava "ja existe".
         */
        await this.refreshSelections(this.companyForSelections);
        // Os atletas oferecidos dependem da empresa, e as selecoes validas dependem
        // deles: as duas coisas se refazem antes de qualquer uso.
        await this.loadCandidates();
        this.showSelections();
        // A selecao aberta e a primeira VALIDA aqui - e se a que veio no endereco
        // nao valer neste contexto, ela nao e adotada.
        // Sem grupo (ou com um que nao vale aqui), o relatorio e de TODOS (B-44): ele
        // nao adota mais o primeiro grupo salvo sozinho.
        const valid = this.selections.map((selection) => selection.id);
        if (this.at.selectionId !== undefined && !valid.includes(this.at.selectionId)) {
            this.at.selectionId = undefined;
        }
        this.focusPatient();
        this.showChoices();
        if (this.scope.picking) {
            // O painel de escolha esta aberto: seus nomes vem da empresa nova.
            this.checked = new Set(this.selections.find((s) => s.id === this.at.selectionId)?.patientIds ?? this.adhoc);
            this.showCandidates();
        }
        await this.load();
    }
    /**
     * O colaborador do endereco vira o unico marcado - a cada `settle`, e nao so na
     * chegada, porque `loadCandidates` volta a "todos" quando a lista e refeita.
     *
     * Cai quando nao vale aqui: com grupo aberto o grupo manda, e quem nao esta entre
     * os oferecidos (outra empresa, link velho) nao tem noite que o servidor devolva.
     */
    focusPatient() {
        const patientId = this.at.patientId;
        if (patientId === undefined) {
            return;
        }
        if (this.at.selectionId !== undefined || !this.candidates.some((c) => c.patientId === patientId)) {
            this.at.patientId = undefined;
            return;
        }
        this.adhoc = new Set([patientId]);
    }
    /** O colaborador em foco, quando ha - ver `focusPatient`. */
    get focused() {
        const patientId = this.at.selectionId === undefined ? this.at.patientId : undefined;
        return patientId === undefined ? undefined : this.candidates.find((c) => c.patientId === patientId);
    }
    /**
     * O intervalo que o relatorio vai pedir.
     *
     * Um atalho calcula o seu; `livre` usa o que veio do endereco. O teto de cinco
     * semanas e aplicado aqui, e nao em cada chamador - assim a tela, a consulta e o
     * PDF falam do mesmo periodo.
     */
    /** O intervalo em vigor - quem o calcula e o seletor. */
    get range() {
        return this.period.range;
    }
    showChoices() {
        // O seletor calcula o intervalo e move o proprio scope; daqui so chega o
        // que o endereco disse.
        this.period.show(this.at.period, this.at.from, this.at.to);
        this.scope.selectionId = this.at.selectionId;
        for (const tab of this.scope.tabs) {
            tab.active = tab.key === this.at.table;
        }
        this.scope.layout = this.layout;
        // Com um colaborador em foco, "Todos" nao e o que esta na tela.
        for (const option of this.scope.selections) {
            option.active =
                option.value === ''
                    ? this.at.selectionId === undefined && !this.focused
                    : Number(option.value) === this.at.selectionId;
        }
        const current = this.selections.find((s) => s.id === this.at.selectionId);
        this.scope.selectionName =
            current?.name ?? this.focused?.name ?? (this.candidates.length > 0 ? ALL_ATHLETES : '');
        this.scope.selectionSize = current?.patientIds.length ?? this.adhoc.size;
    }
    /**
     * A empresa a que um elenco pertence.
     *
     * Para quem ADMINISTRA e a do filtro da propria tela (`at.companyId`); para o
     * PROFISSIONAL e a do cabecalho, que e onde ele troca de empresa. Sao dois lugares
     * porque sao dois controles: o administrador escolhe a empresa como filtro do
     * relatorio, e o profissional a escolhe como o contexto em que esta.
     */
    /**
     * A disposicao em vigor. Sem escolha do leitor, depende do MODO: sem grupo, que
     * pode ser a empresa inteira, um atleta por linha - e o que aguenta centenas de
     * pessoas; com grupo, uma noite por linha, como no PDF do cliente.
     */
    get layout() {
        return this.at.layout ?? (this.at.selectionId === undefined ? 'atletas' : 'noites');
    }
    /** O nome da empresa do relatorio - o titulo do PDF, no lugar do nome do grupo. */
    get companyName() {
        if (this.scope.companyChooser) {
            return this.selector.scope.hasSelection ? this.selector.scope.summary : '';
        }
        const id = this.companyForSelections;
        const company = this.owner?.myCompanies.find((candidate) => candidate.id === id);
        return company ? company.nomeFantasia || company.razaoSocial : '';
    }
    get companyForSelections() {
        return this.at.companyId ?? this.owner?.companyInScope;
    }
    /**
     * Um elenco pertence a uma EMPRESA, e sem ela nao ha o que salvar.
     *
     * O servidor recusa - a coluna e obrigatoria desde `V20260918_2` -, e deixar o
     * pedido ir para receber 400 de volta seria pedir ao leitor que descobrisse pelo
     * erro o que a tela podia ter dito antes. O caso acontece com o administrador geral
     * que nao escolheu empresa: para ele "nenhuma" quer dizer todas, e um elenco de
     * todas as empresas nao existe.
     */
    get missingCompanyForSelection() {
        return this.companyForSelections === undefined;
    }
    // ========== REDE ==========
    /**
     * Os elencos da empresa dada, quando ela nao e a que ja esta em maos.
     *
     * A empresa vem por argumento porque o `at` ainda nao foi escrito quando a chamada
     * acontece na chegada: e justamente a empresa NOVA que decide se ha o que buscar.
     */
    async refreshSelections(companyId) {
        const assinatura = String(companyId ?? '');
        if (assinatura === this.selectionsOf) {
            return;
        }
        this.selectionsOf = assinatura;
        // Sem empresa nao ha elenco: o servidor recusa ("Escolha a empresa desta
        // selecao"), e pedir so produzia um 400 no console.
        if (companyId === undefined) {
            this.selections = [];
            this.showSelections();
            return;
        }
        await this.loadSelections(companyId);
    }
    async loadSelections(companyId = this.companyForSelections) {
        try {
            this.selections = await api.fetchPatientSelections(companyId);
        }
        catch (caught) {
            LOG.warn('Could not load the saved selections', caught);
            this.selections = [];
        }
        this.showSelections();
    }
    /**
     * As selecoes que valem no contexto atual.
     *
     * Uma selecao e oferecida quando TODOS os seus atletas estao na lista de quem se
     * pode escolher agora. Isso a prende a empresa sem guardar empresa nenhuma - e a
     * diferenca importa: se um atleta trocar de time, a selecao deixa de valer para o
     * time antigo sozinha, enquanto uma coluna `company_id` guardada envelheceria
     * calada e o relatorio sairia com um nome que nao pertence mais ali.
     *
     * Para o profissional isso nao filtra nada: os candidatos dele sao todos os
     * pacientes dele, entao todas as suas selecoes passam.
     */
    showSelections() {
        const available = new Set(this.candidates.map((candidate) => candidate.patientId));
        const valid = this.selections.filter((selection) => selection.patientIds.length > 0 && selection.patientIds.every((patientId) => available.has(patientId)));
        // "Todos" vem primeiro e e o padrao (B-44): o relatorio nao exige mais um grupo
        // salvo. So existe quando ha atletas para oferecer.
        const all = this.candidates.length > 0 ? [this.optionOf('', `${ALL_ATHLETES} (${this.candidates.length})`)] : [];
        this.scope.selections = [
            ...all,
            ...valid.map((selection) => this.optionOf(String(selection.id), `${selection.name} (${selection.patientIds.length})`))
        ];
    }
    /**
     * Os atletas que podem entrar no relatorio.
     *
     * A origem muda com o papel, como nas duas telas que ja listam gente: admin ve
     * os colaboradores, profissional ve os monitoramentos que sao dele. Nao e
     * conveniencia - e o mesmo recorte que o servidor vai aplicar na hora de ler o
     * sono, entao oferecer outra coisa seria oferecer o que vai ser recusado.
     */
    async loadCandidates() {
        const professional = this.owner?.allows(['ROLE_PROFESSIONAL']) ?? false;
        const companyAdmin = this.owner?.allows(['ROLE_COMPANY_ADMIN']) ?? false;
        /*
         * Administrador GERAL, e nao "quem escolhe empresa".
         *
         * E o papel que decide o ENDPOINT: `'all'` exige `ROLE_ADMIN`, e quem
         * administra empresas vai por `'company'`. As duas perguntas davam a mesma
         * resposta enquanto um admin de empresa tinha uma so; com duas, usar "escolhe
         * empresa" aqui mandaria ele ao endpoint do super admin e levaria 403.
         */
        const platformAdmin = this.owner?.allows(['ROLE_ADMIN', 'ROLE_SUPER_ADMIN']) ?? false;
        // Quem ESCOLHE empresa e nao escolheu nao recebe lista nenhuma - e o que
        // impede um relatorio com atletas de times diferentes.
        if ((this.owner?.canChooseCompany ?? false) && this.at.companyId === undefined) {
            this.candidates = [];
            this.adhoc = new Set();
            return;
        }
        /*
         * A assinatura leva a EMPRESA tambem para o profissional.
         *
         * Ela e o que evita repetir a consulta quando nada mudou - e desde 17/09/2026 a
         * lista dele depende da empresa em vigor, entao "proprio" fixo deixaria a
         * selecao da empresa anterior na tela depois de trocar.
         */
        const signature = `${platformAdmin || companyAdmin ? this.at.companyId : `proprio:${this.owner?.companyServedInScope ?? ''}`}`;
        if (signature === this.lastCandidates) {
            return;
        }
        try {
            const page = platformAdmin
                ? await collaboratorsApi.fetchCollaborators({ companyId: this.at.companyId }, 'all')
                : companyAdmin
                    ? // Com o `companyId`: quem administra duas empresas precisa dizer de
                        // qual e a lista, e o servidor recusa agir sem saber.
                        await collaboratorsApi.fetchCollaborators({ companyId: this.at.companyId }, 'company')
                    : professional
                        ? // `companyServedInScope` e nao `companyInScope`: e o mesmo endpoint
                            // dos Monitoramentos, e ele so aceita empresa ATENDIDA.
                            await monitoringApi.fetchSharedPatients({ companyId: this.owner?.companyServedInScope })
                        : { elements: [] };
            this.lastCandidates = signature;
            this.candidates = page.elements
                .map((patient) => ({ patientId: patient.patientId, name: patient.patientName || '-' }))
                .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
        }
        catch (caught) {
            LOG.warn('Could not load the patients', caught);
            this.lastCandidates = undefined;
            this.candidates = [];
        }
        // Uma lista NOVA (outra empresa) volta a "todos": os marcados da anterior sao
        // gente de outro time.
        this.adhoc = new Set(this.candidates.map((candidate) => candidate.patientId));
    }
    /**
     * Quem a tabela mostra, na ordem das linhas (ou colunas).
     *
     * Com grupo, o grupo, na ordem dele. Sem grupo, os marcados - todos, ao abrir -,
     * na ordem alfabetica da lista de atletas.
     */
    get shown() {
        const selection = this.selections.find((s) => s.id === this.at.selectionId);
        if (selection) {
            return selection.patientIds;
        }
        return this.candidates.filter((c) => this.adhoc.has(c.patientId)).map((c) => c.patientId);
    }
    async load() {
        const selection = this.selections.find((s) => s.id === this.at.selectionId);
        if (this.scope.companyChooser && this.at.companyId === undefined) {
            this.tables = [];
            this.scope.table = undefined;
            this.scope.loading = false;
            this.scope.message = 'SEM_EMPRESA';
            return;
        }
        /*
         * O que se PEDE ao servidor: o grupo, ou - sem grupo - todos os atletas da
         * empresa. Desmarcar alguem nao e uma consulta nova: as noites dele ja estao em
         * maos, e a tabela so deixa de mostra-lo.
         */
        const asked = selection ? selection.patientIds : this.candidates.map((c) => c.patientId);
        if (asked.length === 0 || this.shown.length === 0) {
            this.tables = [];
            this.scope.table = undefined;
            this.scope.loading = false;
            this.scope.message = selection
                ? 'SELECAO_VAZIA'
                : this.candidates.length === 0
                    ? 'SEM_ATLETAS'
                    : 'NINGUEM_MARCADO';
            return;
        }
        const { from, to } = this.range;
        const signature = JSON.stringify({ ids: asked, from, to });
        if (signature === this.lastQuery) {
            // As noites ja estao em maos: so quem aparece, ou a aba, mudou.
            this.buildTables(this.nights, from, to);
            return;
        }
        const token = ++this.pending;
        this.scope.loading = true;
        this.scope.message = undefined;
        try {
            const nights = await api.fetchSleepReport(asked, { from, to }, this.companyForSelections);
            if (token !== this.pending) {
                return;
            }
            this.lastQuery = signature;
            this.nights = nights;
            this.buildTables(nights, from, to);
        }
        catch (caught) {
            if (token !== this.pending) {
                return;
            }
            this.lastQuery = undefined;
            this.tables = [];
            this.scope.table = undefined;
            this.scope.message = 'ERRO';
            this.app.unexpected('Relatório de sono', caught);
        }
        finally {
            if (token === this.pending) {
                this.scope.loading = false;
            }
        }
    }
    buildTables(nights, from, to) {
        // A ORDEM e a de `shown`: o relatorio de uma semana tem de ter as mesmas
        // pessoas no mesmo lugar que o da anterior.
        const shown = new Set(this.shown);
        const columns = [...shown].map((patientId) => ({
            patientId,
            name: this.candidates.find((c) => c.patientId === patientId)?.name ?? `#${patientId}`
        }));
        const byAthlete = new Map();
        for (const night of nights) {
            if (!shown.has(night.patientId)) {
                continue;
            }
            const list = byAthlete.get(night.patientId) ?? [];
            list.push(night);
            byAthlete.set(night.patientId, list);
        }
        this.tables = buildSleepReport(columns, daysBetween(from, to), byAthlete);
        const empty = this.tables.every((table) => table.rows.every((row) => row.cells.every((cell) => !cell.text)));
        this.scope.message = empty ? 'SEM_DADOS' : undefined;
        this.scope.selectionSize = shown.size;
        this.showTable();
    }
    /** Move para o scope a tabela da aba aberta. As outras cinco seguem em maos. */
    showTable() {
        const data = this.tables.find((table) => table.key === this.at.table);
        if (!data) {
            this.scope.table = undefined;
            return;
        }
        // A MESMA aba, na mesma disposicao, reaproveita a tabela que esta na tela;
        // trocar de aba ou girar a tabela e outra tabela.
        const identity = `${data.key}:${this.layout}`;
        const grid = gridOf(data, this.layout);
        const current = this.scope.table;
        const scope = current && current.identity === identity ? current : new SleepTableScope();
        scope.identity = identity;
        scope.title = data.title;
        scope.phase = data.phase;
        scope.legend = data.legend;
        scope.corner = grid.corner;
        scope.columns = reuseRows(current?.columns, grid.headers, (header) => header.id, (header, reuse) => {
            const columnScope = reuse ?? new SleepColumnScope();
            columnScope.identity = header.id;
            columnScope.name = header.label;
            columnScope.selectable = header.patientId !== undefined;
            columnScope.onSelect =
                header.patientId !== undefined
                    ? this.action(this.onOpenAthlete.bind(this, header.patientId))
                    : Scope.ASYNC_ACTION;
            columnScope.update = this.update;
            return columnScope;
        });
        // As linhas que ja estao na tela sao atualizadas, e nao recriadas - ver `reuseRows`.
        scope.rows = reuseRows(current?.rows, grid.lines, (line) => line.id, (line, reuse) => {
            const rowScope = reuse ?? new SleepRowScope();
            rowScope.identity = line.id;
            rowScope.label = line.label;
            rowScope.cells = line.cells;
            rowScope.selectable = line.patientId !== undefined;
            rowScope.onSelect =
                line.patientId !== undefined
                    ? this.action(this.onOpenAthlete.bind(this, line.patientId))
                    : Scope.ASYNC_ACTION;
            rowScope.update = this.update;
            return rowScope;
        });
        this.scope.table = scope;
    }
    // ========== O QUE A TELA CHAMA ==========
    handleSearchChanged(value) {
        this.scope.search = value;
        this.showCandidates();
    }
    showCandidates() {
        const needle = this.scope.search.trim().toLowerCase();
        this.scope.candidates = this.candidates
            .filter((candidate) => !needle || candidate.name.toLowerCase().includes(needle))
            .map((candidate) => {
            const scope = new SleepCandidateScope();
            scope.identity = candidate.patientId;
            scope.patientId = candidate.patientId;
            scope.name = candidate.name;
            scope.checked = this.checked.has(candidate.patientId);
            scope.onToggle = () => this.toggleCandidate(candidate.patientId);
            return scope;
        });
        this.scope.checkedCount = this.checked.size;
    }
    toggleCandidate(patientId) {
        if (this.checked.has(patientId)) {
            this.checked.delete(patientId);
        }
        else {
            this.checked.add(patientId);
        }
        this.showCandidates();
        this.followChecked();
    }
    /**
     * Sem grupo, marcar e desmarcar MUDA o relatorio na hora (B-44, relato 11). Com
     * grupo, o painel e um rascunho: so salvar o aplica.
     *
     * Nao vai a rede - as noites de todos ja estao em maos - e nao vai ao endereco.
     */
    followChecked() {
        if (this.at.selectionId !== undefined) {
            return;
        }
        // Mexer em quem aparece encerra o foco num colaborador: a escolha agora e a do
        // painel, e o endereco deixa de dizer que e um so.
        if (this.at.patientId !== undefined) {
            this.at.patientId = undefined;
            this.showChoices();
            this.updateHistory();
        }
        this.adhoc = new Set(this.checked);
        this.scope.selectionSize = this.adhoc.size;
        this.load().catch((caught) => this.app.unexpected('Relatório de sono', caught));
    }
    async onTogglePicking() {
        this.scope.picking = !this.scope.picking;
        if (this.scope.picking) {
            // Abre com o que ja esta selecionado, para EDITAR ser o caminho curto.
            const current = this.selections.find((s) => s.id === this.at.selectionId);
            this.checked = new Set(current?.patientIds ?? this.adhoc);
            this.scope.search = '';
            this.showCandidates();
        }
    }
    async onSelectAll() {
        for (const candidate of this.candidates) {
            this.checked.add(candidate.patientId);
        }
        this.showCandidates();
        this.followChecked();
    }
    async onClearAll() {
        this.checked.clear();
        this.showCandidates();
        this.followChecked();
    }
    /**
     * Salva a selecao aberta, ou cria uma nova quando o nome mudou.
     *
     * Nome novo cria, nome igual atualiza. E o comportamento que deixa "duplicar
     * para ajustar" ser uma acao so - renomear e salvar.
     */
    async onSaveSelection(name) {
        const trimmed = name.trim();
        if (!trimmed || this.checked.size === 0) {
            return;
        }
        if (this.missingCompanyForSelection) {
            this.app.alert('warning', 'Escolha a empresa', 'Um elenco pertence a uma empresa. Escolha qual antes de salvar.');
            return;
        }
        this.scope.saving = true;
        try {
            const current = this.selections.find((s) => s.id === this.at.selectionId);
            const ids = [...this.checked];
            const saved = current && current.name === trimmed
                ? await api.updatePatientSelection(current.id, trimmed, ids)
                : await api.createPatientSelection(trimmed, ids, this.companyForSelections);
            await this.loadSelections();
            this.scope.picking = false;
            this.lastQuery = undefined;
            await this.go({ selectionId: saved.id });
        }
        catch (caught) {
            this.app.unexpected('Salvar a seleção', caught);
        }
        finally {
            this.scope.saving = false;
        }
    }
    async onDeleteSelection() {
        const current = this.selections.find((s) => s.id === this.at.selectionId);
        if (!current) {
            return;
        }
        this.app.confirm('Apagar seleção', `A seleção "${current.name}" será apagada. Os atletas e as medições não são afetados.`, 'Apagar', async () => {
            try {
                await api.deletePatientSelection(current.id);
                await this.loadSelections();
                this.lastQuery = undefined;
                await this.go({ selectionId: this.selections[0]?.id });
            }
            catch (caught) {
                this.app.unexpected('Apagar a seleção', caught);
            }
        }, { cancelText: 'Cancelar', icon: 'trash-can-outline' });
    }
    /**
     * Gira a tabela. E estado desta tela, e nao viagem: a mesma consulta, lida pelo
     * outro lado - o `go` nao vai a rede, so redesenha e escreve o endereco.
     */
    async onToggleLayout() {
        await this.go({ layout: this.layout === 'noites' ? 'atletas' : 'noites' });
    }
    async onDownload() {
        if (this.tables.length === 0 || this.scope.downloading) {
            return;
        }
        const people = this.shown.length;
        if (people > SLEEP_PDF_MAX_PEOPLE) {
            this.app.alert('warning', 'PDF com até 100 pessoas', `O relatório tem ${people} pessoas. Abra "Montar seleção" e deixe marcadas até ${SLEEP_PDF_MAX_PEOPLE} para baixar o PDF.`);
            return;
        }
        this.scope.downloading = true;
        try {
            // A biblioteca de PDF desce AGORA, e nao na abertura do painel: ela pesa
            // mais que a tela inteira e so serve a quem clica aqui.
            const { downloadSleepReport } = await import('./sleep-report.pdf');
            await downloadSleepReport({
                tables: this.tables,
                // O PDF sai na disposicao da tela (B-36).
                layout: this.layout,
                // O nome da EMPRESA, e nao o do grupo (relato 11): o grupo costuma ser um
                // codigo para separar pessoas, e nao diz nada a quem le o relatorio.
                title: this.companyName,
                rangeLabel: this.period.scope.rangeLabel,
                periodLabel: PERIOD_LABELS[this.at.period]
            });
        }
        catch (caught) {
            this.app.unexpected('Gerar o PDF', caught);
        }
        finally {
            this.scope.downloading = false;
        }
    }
    // ========== FABRICAS ==========
    optionOf(value, label) {
        const scope = new SleepOptionScope();
        scope.identity = value;
        scope.value = value;
        scope.label = label;
        // So as SELECOES passam por aqui: o periodo tem seletor proprio desde que
        // o `PeriodSelectorPresenter` existe.
        scope.onSelect = this.action(async () => {
            // "Todos" com um colaborador em foco volta a TODOS: os marcados eram so ele.
            if (value === '' && this.focused) {
                this.adhoc = new Set(this.candidates.map((c) => c.patientId));
            }
            await this.go({ selectionId: value === '' ? undefined : Number(value), patientId: undefined });
        });
        return scope;
    }
    /**
     * Abre o detalhe do atleta cujo nome foi clicado no cabecalho da tabela.
     *
     * As pessoas sao as COLUNAS deste relatorio, entao o "clicar sobre um individuo"
     * do pedido e o cabecalho. O `patientId` ja vinha calculado desde o dominio
     * (`SleepReportColumn`) e era descartado ao montar o scope.
     *
     * `of` e nao copia campo a campo, como em toda viagem deste painel: o que os
     * presenters vivos publicam viaja junto, e so o paciente e novidade. Sem `row`
     * aqui - o relatorio tem o nome, e nao a linha inteira da lista -, entao o
     * detalhe abre buscando, que e o mesmo caminho de quem chega por link.
     */
    async onOpenAthlete(patientId) {
        const target = CollaboratorDetailKeys.of(this.app);
        target.patientId = patientId;
        await target.flip();
    }
    tabOf(key) {
        const scope = new SleepTabScope();
        scope.identity = key;
        scope.key = key;
        scope.label = TAB_LABELS[key];
        scope.phase = phaseOfTable(key);
        scope.onSelect = this.action(async () => {
            await this.go({ table: key });
        });
        return scope;
    }
}
/**
 * O rotulo da ABA, mais curto que o titulo da tabela.
 *
 * Seis abas com "Duração do sono profundo" por extenso nao cabem lado a lado, e o
 * titulo completo ja esta no cabecalho da tabela logo abaixo.
 */
const TAB_LABELS = {
    deitou: 'Deitou',
    acordou: 'Acordou',
    rem: 'REM',
    profundo: 'Profundo',
    total: 'Total',
    leve: 'Leve'
};
//# sourceMappingURL=sleep-report.presenter.js.map